/**
 * AppShell — the header, nav, and page frame from wireframes 3c / 3d.
 * @file src/web/shared/components/AppShell.tsx
 *
 * @llm-rule WHEN: Wrapping any signed-in page
 * @llm-rule AVOID: Building a second nav — the tab set is derived from the viewer's rank here
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, ListChecks, Plane, ScanFace } from 'lucide-react';
import { useAuth } from '../../features/auth';
import { PRODUCT } from '../brand';
import { isSuperAdmin, isSupervisor, type OrgRole } from '../domain';
import { useAppRequest } from '../hooks/useAppApi';
import { AccountMenu } from './AccountMenu';
import { BottomNav, type BottomTab } from './BottomNav';
import { manageItemsFor } from './ManageMenu';

interface Tab {
  label: string;
  href: string;
  /** Shown in the mobile bottom bar. A tab bar without icons reads as a web page. */
  icon: BottomTab['icon'];
  /** Minimum rank needed to see this tab. Omitted means everyone. */
  minRole?: OrgRole;
  /** Extra predicate for tabs that aren't a simple rank floor. */
  when?: (role: OrgRole) => boolean;
}

/**
 * Labels say what the screen IS, not what we called the feature internally.
 * "Threads" and "Org" both tested badly — nobody could tell "Org" from "Team",
 * so they are now "Company" (everyone) and "My Team" (your reports).
 */
/**
 * The tabs are YOUR DAY, and they are the same four for everyone — a Company Admin
 * clocks in exactly like a employee does.
 *
 * Everything for RUNNING the company (people, offices, oversight, reports,
 * authorities) sits behind the single "Manage" entry beside them, and anything
 * about YOU is in the avatar menu. Before this split a Company Admin had seven tabs plus
 * six avatar items, with leadership tools scattered across both.
 */
const TABS: Tab[] = [
  { label: 'Clock in', href: '/clock', icon: ScanFace },
  { label: 'Calendar', href: '/calendar', icon: CalendarDays },
  // Everyone sees Leave: those below the top to APPLY, those above to
  // APPROVE. Hiding it from the Company Admin left nobody able to sign anything off.
  { label: 'Leave', href: '/leave', icon: Plane },
  { label: 'Tasks', href: '/tasks', icon: ListChecks },
];

export function AppShell({
  children,
  title,
  subtitle,
  wide = false,
  flush = false,
}: {
  children: ReactNode;
  /** Big greeting line. Falls back to the brand name. */
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Consoles want the full width; the phone-shaped clock-in card does not. */
  wide?: boolean;
  /**
   * Full-bleed: drops the vertical padding and footer so a page can own the
   * remaining viewport height. Used by the chat screen, which sizes itself
   * against `--shell-offset` below.
   */
  flush?: boolean;
}) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useAppRequest();

  const role = (user?.orgRole as OrgRole) ?? 'EMPLOYEE';

  // The company name sits beside the product name in the header, so a person
  // in two tenants can always tell which one they're looking at.
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);

  useEffect(() => {
    if (!user || isSuperAdmin(role)) return;
    void request<{ organisation: { name: string; logo?: string | null } | null }>(
      '/api/organisation/mine',
    ).then((r) => {
      if (!r.ok || !r.data.organisation) return;
      setCompanyName(r.data.organisation.name);
      setCompanyLogo(r.data.organisation.logo ?? null);
    });
  }, [user, role, request]);

  useEffect(() => {
    document.title = companyName ? `${PRODUCT.name} · ${companyName}` : PRODUCT.name;
  }, [companyName]);

  /*
    The number on the Tasks tab.

    Re-read on every navigation rather than once on mount: starting or closing a
    task changes it, and a badge that keeps claiming three outstanding tasks
    after you have cleared them is worse than no badge. `/api/task/counts`
    exists precisely so this costs two COUNT queries, not 200 rows.
  */
  const [taskCounts, setTaskCounts] = useState<{ actionable: number; overdue: number } | null>(
    null,
  );

  useEffect(() => {
    if (!user || isSuperAdmin(role)) return;
    void request<{ open: number; overdue: number; actionable: number }>('/api/task/counts').then(
      (r) => {
        if (r.ok) setTaskCounts({ actionable: r.data.actionable, overdue: r.data.overdue });
      },
    );
  }, [user, role, request, location.pathname]);

  // A platform operator has no company app. Bounce rather than render an
  // empty shell whose every request would 403.
  if (isSuperAdmin(role)) return <Navigate to="/platform" replace />;

  /*
    What this viewer can reach under "Manage".

    Gated on the LIST, not on rank. A Manager holds two of these entries —
    Company today and Monthly report, both `minRole: 'MANAGER'` — so gating the
    tab at Head hid destinations the Manager is explicitly allowed to open.
    That was invisible while the account menu still listed them, and became a
    dead end the moment those were removed from the header on mobile.

    manageItemsFor() already decides what each rank may open, so asking it is
    the only way this cannot drift from the menu it fills.
  */
  const manageItems = manageItemsFor(role);

  const visibleTabs = TABS.filter((tab) => {
    if (tab.when && !tab.when(role)) return false;
    if (!tab.minRole) return true;
    if (tab.minRole === 'MANAGER') return isSupervisor(role);
    if (tab.minRole === 'HEAD') return role === 'HEAD' || role === 'COMPANY_ADMIN';
    return true;
  });

  return (
    <div className="min-h-screen bg-paper-warm">
      {/*
        Sticky and translucent. The header is the one element on screen at all
        times, so it stays put and lets the page move under it — the frosted
        backdrop keeps the nav legible over whatever scrolls beneath.
      */}
      <header className="sticky top-0 z-40 border-b border-rule bg-paper/80 backdrop-blur-xl backdrop-saturate-150">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3">
          <Link to="/clock" className="group flex items-center gap-2.5">
            {/* The company's own mark when they have uploaded one. `contain`
                rather than `cover` because a logo cropped to fill a square is a
                logo nobody recognises. */}
            {companyLogo ? (
              <span className="grid size-[30px] flex-none place-items-center overflow-hidden rounded-[9px] bg-paper-sunk transition-transform group-hover:scale-105">
                <img
                  src={companyLogo}
                  alt={companyName ?? 'Company logo'}
                  className="size-full object-contain"
                />
              </span>
            ) : (
              <span
                className="grid size-[30px] flex-none place-items-center rounded-[9px] text-white transition-transform group-hover:scale-105"
                style={{ background: 'var(--brand-ramp)', boxShadow: 'var(--elev-1)' }}
              >
                <ScanFace className="size-[17px]" strokeWidth={1.9} />
              </span>
            )}
            {/* The company name is shown at every width: on a phone it is the
                only thing telling you WHICH company you are signed into, so
                hiding it below `sm` removed it exactly where it mattered most.
                Capped and truncated so a long name cannot push the account
                menu off the edge. */}
            <span className="min-w-0 leading-tight">
              <span className="block text-[15px] font-semibold tracking-tight text-ink">
                {PRODUCT.name}
              </span>
              <span className="block max-w-[42vw] truncate text-[10.5px] text-ink-faint sm:max-w-none">
                {companyName ?? PRODUCT.tagline}
              </span>
            </span>
          </Link>

          {/*
            The account menu MUST live outside this nav. `overflow-x-auto`
            makes overflow-y compute to `auto` as well, and the nav is only
            ~32px tall — so an absolutely positioned dropdown inside it is
            clipped to nothing. It still exists in the DOM, which is what made
            this look fine in a naive test while being invisible and
            unclickable in the browser.
          */}
          <div className="flex min-w-0 items-center gap-4">
            {/*
              On a phone the tabs used to sit in a sideways-scrolling strip, so
              half the app was hidden behind a swipe nobody knew was there. Below
              `md` they collapse into the button on the right instead.
            */}
            <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
              {visibleTabs.map((tab) => {
                const active = location.pathname.startsWith(tab.href);
                return (
                  <Link
                    key={tab.href}
                    to={tab.href}
                    aria-current={active ? 'page' : undefined}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-all ${
                      active
                        ? 'bg-paper-sunk text-ink shadow-[inset_0_0_0_1px_var(--rule)]'
                        : 'text-ink-faint hover:bg-paper-sunk/60 hover:text-ink'
                    }`}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </nav>

            {/*
              ONE menu, not two.
              A "Manage" dropdown next to the avatar dropdown meant two triggers
              sitting side by side with no way to tell which held what — people
              opened both looking for the same thing. The company tools now live
              inside the account menu under their own heading, so there is a
              single place for "everything that is not a tab".
            */}
            <div className="flex-none">
              <AccountMenu />
            </div>

          </div>
        </div>
      </header>

      {/*
        A fixed bar is out of flow, so the page must reserve its height or the
        last card on every screen sits permanently underneath it. The reserve is
        harmless above `md`, where the bar is hidden and the tabs are in the header.
      */}
      <main
        className={`pad-for-tabbar mx-auto px-5 ${flush ? 'py-4' : 'py-6'} ${
          wide ? 'max-w-6xl' : 'max-w-md'
        }`}
      >
        {(title || subtitle) && (
          <div className="anim-rise mb-6">
            {title && (
              <h1 className="text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] text-ink">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-2 max-w-[68ch] text-[13.5px] leading-relaxed text-ink-soft">
                {subtitle}
              </p>
            )}
          </div>
        )}
        <div className="anim-rise" style={{ ['--d' as string]: '70ms' }}>
          {children}
        </div>
      </main>

      {!flush && (
      <footer className="pad-for-tabbar mx-auto max-w-6xl px-5 pb-8 pt-2 text-[11.5px] text-ink-ghost">
        {PRODUCT.name} · one enrolment photo on file; clock-ins send no image —{' '}
        <button
          type="button"
          className="underline underline-offset-2 hover:text-ink-faint"
          onClick={() => navigate('/privacy')}
        >
          how this works
        </button>
      </footer>
      )}

      {/*
        THE mobile navigation. Below `md` the header keeps only identity (logo,
        company, avatar) and every destination lives down here under the thumb.
      */}
      <BottomNav
        tabs={visibleTabs.map((tab) =>
          tab.href === '/tasks'
            ? {
                ...tab,
                badge: taskCounts?.actionable ?? 0,
                badgeAlert: (taskCounts?.overdue ?? 0) > 0,
              }
            : tab,
        )}
        manageItems={manageItems}
        currentPath={location.pathname}
      />
    </div>
  );
}
